import React, { useState, useEffect, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { Outlet, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Alert, Stack } from "@mui/material";
import OfferBoard from "./OfferBoard/OfferBoard";
import OfferBoardsSidebar from "./OfferBoardsSidebar/OfferBoardsSidebar";
import LoadDetails from "./LoadDetails/LoadDetails";
import LoadProfileLaneHistory from "../LoadProfile/LoadProfileLaneHistory/LoadProfileLaneHistory";
import ShotClock from "../ShotClock/ShotClock";
import PrimaryTabs from "../TabPanels/PrimaryTabPanel/PrimaryTabs/PrimaryTabs";
import PrimaryTabPanel from "../TabPanels/PrimaryTabPanel/PrimaryTabPanel";
import PrimarySelect from "../Inputs/PrimarySelect/PrimarySelect";
import SecurityCheck from "../SecurityCheck/SecurityCheck";
import useNotificationsReceiver from "../../hooks/useNotificationsReceiver/useNotificationsReceiver";
import { MAXPAY_UPDATED_EVENT } from "../../constants/syncConstants";
import { LoadService } from "../../services/LoadService";
import { MovementService } from "../../services/MovementService";
import { NotificationsService } from "../../services/NotificationsService";
import { ActiveUserContext, AlertContext, UserPermissionsContext } from "../Dashboard/Dashboard";

export default function OfferBoards({
  isLoading,
  carrierLoads,
  newItem,
  setRefreshLoads,
  regions,
  customers,
  selectedRegions,
  selectedCustomers,
  setSelectedRegions,
  setSelectedCustomers,
  isAdminUser,
  refreshPage,
  setRefreshPage,
  onLoadFieldUpdate,
  handleTableAction
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  const userPermissions = useContext(UserPermissionsContext);
  let [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [activeLoad, setActiveLoad] = useState({});
  const [maxPayInfo, setMaxPayInfo] = useState({});
  const [maxPay, setMaxPay] = useState(null);
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [availableToBook, setAvailableToBook] = useState(false);
  const [refreshOffers, setRefreshOffers] = useState(false);
  const { newOfferNotifications, getNewOfferNotifications } = useNotificationsReceiver(userPermissions);

  //Admins
  const [counter, setCounter] = useState(null);

  const tabLabels = useMemo(() => [
    {
      name: "Offers"
    },
    {
      name: "History"
    }
  ], []);

  const loads = useMemo(() => carrierLoads, [carrierLoads]);

  const getLoad = () => {
    if (selectedOrderId && carrierLoads && carrierLoads.length > 0) {
      setIsLoadLoading(true);
      LoadService.GetLoad(instance, selectedOrderId).then(res => {
        res.newlyAdded = carrierLoads.find(({ orderId }) => orderId === selectedOrderId)?.newlyAdded;
        setActiveLoad(res);
        setIsLoadLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoadLoading(false);
        warning(error);
      });
    } else {
      setSelectedOrderId("");
      setActiveLoad({});
    }
  };

  const getMaxPay = () => {
    MovementService.GetMaxPay(instance, selectedOrderId).then(res => {
      setMaxPayInfo(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  const handleSelectOrder = id => {
    setSearchParams({ loadId: id });
  };

  const updateNotifications = () => {
    if (newOfferNotifications.find(o => o.orderId === selectedOrderId)) {
      NotificationsService.UpdateAllNotificationsAsRead(instance, activeUser.userIdMcld, "NewOfferNotification", selectedOrderId).then(() => {
        getNewOfferNotifications();
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    }
  };

  useEffect(() => {
    setCounter(null);
  }, [maxPay]);

  useEffect(() => {
    const loadId = searchParams.get("loadId");
    const loadExists = carrierLoads.find(({ orderId }) => orderId === (loadId || selectedOrderId));

    if (!loadExists && carrierLoads.length > 0) {
      const order = carrierLoads.find(({ status }) => status === "pending") || carrierLoads[0];

      setSelectedOrderId(order.orderId);
      setSearchParams({ loadId: order.orderId });
    } else if (loadExists && !loadId) {
      setSearchParams({ loadId: selectedOrderId });
    } else {
      setSelectedOrderId(loadId);
    }
  }, [carrierLoads, searchParams]);

  useEffect(() => {
    if (carrierLoads.length) {
      carrierLoads.forEach(load => {
        if (newOfferNotifications.find(o => o.orderId === load.orderId)) {
          load.newlyAdded = true;
        } else {
          load.newlyAdded = false;
        }
      });
    }
  }, [carrierLoads, newOfferNotifications]);

  useEffect(() => {
    if (selectedOrderId) {
      getLoad();
      getMaxPay();

      if (isAdminUser) updateNotifications();
    }
  }, [selectedOrderId]);

  useEffect(() => {
    if (newItem && newItem?.orderId === selectedOrderId) {
      getLoad();
      getMaxPay();
    }
  }, [newItem]);

  useEffect(() => {
    if (refreshPage) {
      setRefreshPage(false);
      getLoad();
      getMaxPay();
    }
  }, [refreshPage]);

  useEffect(() => {
    const handleMaxPayUpdated = event => {
      const { orderId, maxPay: nextMaxPay } = event?.detail ?? {};
      if (!orderId || orderId !== selectedOrderId) {
        return;
      }

      setActiveLoad(old => {
        if (!old?.id || old.id !== orderId) {
          return old;
        }
        return {
          ...old,
          maxPay: nextMaxPay,
        };
      });
      setMaxPay(nextMaxPay);
      getMaxPay();
    };

    window.addEventListener(MAXPAY_UPDATED_EVENT, handleMaxPayUpdated);
    return () => window.removeEventListener(MAXPAY_UPDATED_EVENT, handleMaxPayUpdated);
  }, [selectedOrderId]);

  useEffect(() => {
    if (activeUser?.userIdMcld) {
      const userSpecificRegionsKey = `phoenix_offerBoards_selectedRegions_${activeUser.userIdMcld}`;
      const userSpecificCustomersKey = `phoenix_offerBoards_selectedCustomers_${activeUser.userIdMcld}`;
      try {
        const storedRegions = localStorage.getItem(userSpecificRegionsKey);
        if (storedRegions) {
          setSelectedRegions(JSON.parse(storedRegions));
        }
        const storedCustomers = localStorage.getItem(userSpecificCustomersKey);
        if (storedCustomers) {
          setSelectedCustomers(JSON.parse(storedCustomers));
        }
      } catch (error) {
        console.error("Error reading offer board selections from local storage", error);
        localStorage.removeItem(userSpecificRegionsKey);
        localStorage.removeItem(userSpecificCustomersKey);
      }
    }
  }, [activeUser?.userIdMcld, setSelectedRegions, setSelectedCustomers]);

  useEffect(() => {
    if (activeUser?.userIdMcld) {
      const userSpecificRegionsKey = `phoenix_offerBoards_selectedRegions_${activeUser.userIdMcld}`;
      try {
        // selectedRegions is PropTypes.array.isRequired, so it will always be an array.
        localStorage.setItem(userSpecificRegionsKey, JSON.stringify(selectedRegions));
      } catch (error) {
        console.error(`Error writing selectedRegions to local storage for key ${userSpecificRegionsKey}:`, error);
      }
    }
  }, [selectedRegions, activeUser?.userIdMcld]);

  useEffect(() => {
    if (activeUser?.userIdMcld) {
      const userSpecificCustomersKey = `phoenix_offerBoards_selectedCustomers_${activeUser.userIdMcld}`;
      try {
        // selectedCustomers is PropTypes.array.isRequired, so it will always be an array.
        localStorage.setItem(userSpecificCustomersKey, JSON.stringify(selectedCustomers));
      } catch (error) {
        console.error(`Error writing selectedCustomers to local storage for key ${userSpecificCustomersKey}:`, error);
      }
    }
  }, [selectedCustomers, activeUser?.userIdMcld]);
  
  return (
    <Box sx={{ margin: "10px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }} >
          <CircularProgress sx={{ color: "primary.60" }} size={50} />
        </Box>
      ) : (
        <Box sx={{ display: "flex" }}>
          <Box sx={{ marginRight: "10px" }}>
            <SecurityCheck
              componentName="CarrierOffersDashboardManager"
              action="Read"
            >
              <PrimarySelect
                label="Region(s)"
                options={regions.map((r) => (r))}
                onChange={setSelectedRegions}
                value={selectedRegions}
                width="160px"
                nameKey="regionName"
                valueKey="regionId"
                error={selectedRegions.length === 0}
                multiple
              />
              <br />
              <PrimarySelect
                label="Customer(s)"
                options={customers.slice().sort((a, b) => (a.customerName || "").localeCompare(b.customerName || ""))}
                onChange={setSelectedCustomers}
                value={selectedCustomers}
                width="160px"
                nameKey="customerName"
                valueKey="customerId"
                error={selectedCustomers.length === 0}
                allOption
                multiple
              />
            </SecurityCheck>
            <OfferBoardsSidebar
              activeUser={activeUser}
              isAdminUser={isAdminUser}
              loads={loads}
              setRefreshLoads={setRefreshLoads}
              setRefreshPage={setRefreshPage}
              selectedOrderId={selectedOrderId}
              setSelectedOrderId={handleSelectOrder}
            />
          </Box>
          <Box sx={{ width: "calc(100% - 170px)" }}>
            {isLoadLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
                <CircularProgress sx={{ color: "primary.60" }} size={50} />
              </Box>
            ) : carrierLoads && carrierLoads.length > 0 ? (
              <>
                <Box sx={{
                  position: "sticky",
                  zIndex: 999,
                  backgroundColor: "white_opacity.100",
                  width: "100%"
                }}>
                  {activeLoad.id &&
                    <>
                      <LoadDetails
                        activeLoad={activeLoad}
                        setActiveLoad={setActiveLoad}
                        maxPayInfo={maxPayInfo}
                        maxPay={maxPay}
                        setMaxPay={setMaxPay}
                        onLoadFieldUpdate={onLoadFieldUpdate}
                        handleTableAction={handleTableAction}
                      />
                      <ShotClock
                        load={activeLoad}
                        setAvailableToBook={setAvailableToBook}
                        actionCallback={handleTableAction}
                      />
                    </>
                  }
                  <Box sx={{ borderBottom: 1, borderColor: "divider", margin: "0 30px 10px 30px" }}>
                    <PrimaryTabs value={tabValue} handleChange={(e, v) => setTabValue(v)} tabLabels={tabLabels} />
                  </Box>
                </Box>
                <PrimaryTabPanel value={tabValue} index={0}>
                  <OfferBoard
                    activeLoad={activeLoad}
                    activeUser={activeUser}
                    isAdminUser={isAdminUser}
                    selectedOrderId={selectedOrderId}
                    counter={counter}
                    setCounter={setCounter}
                    refreshOffers={refreshOffers}
                    setRefreshOffers={setRefreshOffers}
                    setRefreshPage={setRefreshPage}
                    setRefreshLoads={setRefreshLoads}
                    setSearchParams={setSearchParams}
                    onLoadFieldUpdate={onLoadFieldUpdate}
                    newlyAdded={activeLoad.newlyAdded}
                    updateNotifications={updateNotifications}
                    availableToBook={availableToBook}
                    handleTableAction={handleTableAction}
                  />
                </PrimaryTabPanel>
                <PrimaryTabPanel value={tabValue} index={1}>
                  <LoadProfileLaneHistory
                    activeLoad={activeLoad}
                    handleTableAction={handleTableAction}
                  />
                </PrimaryTabPanel>
              </>
            ) : (
              <Stack sx={{ width: "100%" }} spacing={2}>
                <Alert style={{ padding: "10px" }} severity="warning">No offers available.</Alert>
              </Stack>
            )}
          </Box>
        </Box>
      )}
      <Outlet />
    </Box>
  );
}

OfferBoards.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  carrierLoads: PropTypes.array.isRequired,
  newItem: PropTypes.object,
  setRefreshLoads: PropTypes.func.isRequired,
  regions: PropTypes.array.isRequired,
  customers: PropTypes.array.isRequired,
  selectedCustomers: PropTypes.array.isRequired,
  setSelectedCustomers: PropTypes.func.isRequired,
  selectedRegions: PropTypes.array.isRequired,
  setSelectedRegions: PropTypes.func.isRequired,
  isAdminUser: PropTypes.bool,
  refreshPage: PropTypes.bool,
  setRefreshPage: PropTypes.func,
  onLoadFieldUpdate: PropTypes.func,
  handleTableAction: PropTypes.func
};